import { zipSync, type Zippable } from "fflate";

import {
  EXPENSE_CATEGORIES,
  PAYMENT_METHODS,
  VAT_MODES,
  filesOf,
  labelFor,
  type AccExpense,
} from "./accounting";
import {
  buildSummary,
  inPeriod,
  periodLabel,
  suppliersOver347,
  type IncomeRow,
  type Period,
} from "./summary";
import { xlsxFiles, type XlsxSheet } from "./xlsx";

/** "2026-10-05" -> "05/10/2026" */
export const spanishDate = (day?: string | null) =>
  day ? `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}` : "";

const safe = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\w.-]+/g, " ")
    .trim()
    .slice(0, 60);

const extensionOf = (name?: string | null) => {
  const match = name?.match(/\.[A-Za-z0-9]{1,5}$/);
  return match ? match[0].toLowerCase() : "";
};

/** Name of an invoice file inside the package: "003 2026-10-05 Packlink F-123.pdf" */
export const packageFileName = (
  index: number,
  day: string,
  who: string,
  number: string | null | undefined,
  originalName: string | null | undefined,
  part = 1,
) =>
  `${String(index + 1).padStart(3, "0")} ${day} ${safe(who)}${
    number ? ` ${safe(number)}` : ""
  }${part > 1 ? ` (${part})` : ""}${extensionOf(originalName)}`;

export const EXPENSE_FOLDER = "Facturas recibidas";
export const INCOME_FOLDER = "Justificantes de ingresos";

/** Where each file of an expense goes in the package (main file first). */
export const expenseTargets = (expense: AccExpense, index: number) =>
  filesOf(expense).map((file, position) => ({
    path: file.path,
    target: `${EXPENSE_FOLDER}/${packageFileName(
      index,
      expense.invoice_date,
      expense.supplier_name,
      expense.invoice_number,
      file.name,
      position + 1,
    )}`,
  }));

/** The spreadsheets for the accountant: received invoices, income, VAT and totals. */
export const buildGestorSheets = (
  period: Period,
  allExpenses: AccExpense[],
  allIncomes: IncomeRow[],
): XlsxSheet[] => {
  const expenses = allExpenses
    .filter((e) => inPeriod(e.invoice_date, period))
    .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date));
  const incomes = allIncomes
    .filter((row) => inPeriod(row.day, period))
    .sort((a, b) => a.day.localeCompare(b.day));
  const summary = buildSummary(allIncomes, allExpenses, period);
  const manualWithFile = incomes.filter((row) => row.income?.file_path);

  const sheets: XlsxSheet[] = [
    {
      name: "Facturas recibidas",
      columns: [
        { header: "Nº", width: 6 },
        { header: "Fecha factura", width: 12 },
        { header: "Nº factura", width: 16 },
        { header: "Proveedor", width: 30 },
        { header: "NIF", width: 14 },
        { header: "Categoría", width: 30 },
        { header: "Tipo de IVA", width: 26 },
        { header: "Base imponible" },
        { header: "% IVA", width: 8 },
        { header: "Cuota IVA" },
        { header: "Otros sin IVA" },
        { header: "% IRPF", width: 8 },
        { header: "Retención IRPF" },
        { header: "Total" },
        { header: "% deducible", width: 10 },
        { header: "Pagada el", width: 12 },
        { header: "Forma de pago", width: 14 },
        { header: "Concepto", width: 30 },
        { header: "Archivo", width: 40 },
        { header: "Estado", width: 12 },
      ],
      rows: expenses.map((e, index) => [
        index + 1,
        spanishDate(e.invoice_date),
        e.invoice_number ?? "",
        e.supplier_name,
        e.supplier_tax_id ?? "",
        labelFor(EXPENSE_CATEGORIES, e.category),
        labelFor(VAT_MODES, e.vat_mode),
        Number(e.base_amount),
        Number(e.vat_rate),
        Number(e.vat_amount),
        Number(e.other_amount),
        Number(e.irpf_rate),
        Number(e.irpf_amount),
        Number(e.total),
        Number(e.deductible_pct),
        spanishDate(e.paid_at),
        labelFor(PAYMENT_METHODS, e.payment_method),
        e.description ?? "",
        (() => {
          const targets = expenseTargets(e, index);
          if (!targets.length) return "FALTA LA FACTURA";
          return targets.length > 1
            ? `${targets[0].target} (+${targets.length - 1})`
            : targets[0].target;
        })(),
        e.status === "review" ? "Por revisar" : "Revisado",
      ]),
    },
    {
      name: "Ingresos",
      columns: [
        { header: "Fecha", width: 12 },
        { header: "Origen", width: 10 },
        { header: "Cliente", width: 28 },
        { header: "NIF", width: 14 },
        { header: "Concepto", width: 30 },
        { header: "Forma de cobro", width: 14 },
        { header: "Base imponible" },
        { header: "% IVA", width: 8 },
        { header: "Cuota IVA" },
        { header: "Total" },
        { header: "Recibo / justificante", width: 40 },
      ],
      rows: incomes.map((row) => {
        const fileIndex = manualWithFile.indexOf(row);
        return [
          spanishDate(row.day),
          row.source === "stripe" ? "Stripe" : "Manual",
          row.customer,
          row.income?.customer_tax_id ?? "",
          row.concept,
          row.method,
          row.base,
          row.vatRate,
          row.vat,
          row.total,
          row.receiptUrl ??
            (fileIndex >= 0
              ? `${INCOME_FOLDER}/${packageFileName(fileIndex, row.day, row.customer || row.concept, null, row.income?.file_name)}`
              : ""),
        ];
      }),
    },
    {
      name: "Resumen IVA",
      columns: [
        { header: periodLabel(period), width: 60 },
        { header: "Base" },
        { header: "Cuota" },
      ],
      rows: [
        ["IVA DEVENGADO", null, null],
        ...summary.vat.salesByRate.map((rate): (string | number)[] => [
          `Ventas al ${rate.rate} %`,
          rate.base,
          rate.vat,
        ]),
        [
          "Adquisiciones intracomunitarias de servicios (inversión del sujeto pasivo)",
          summary.vat.reverseBase,
          summary.vat.reverseVat,
        ],
        ["Total IVA devengado", null, summary.vat.outputVat],
        [null, null, null],
        ["IVA DEDUCIBLE", null, null],
        [
          "Compras y gastos interiores",
          summary.vat.domesticBase,
          summary.vat.domesticVat,
        ],
        [
          "Adquisiciones intracomunitarias de servicios",
          summary.vat.reverseBase,
          round(summary.vat.deductibleVat - summary.vat.domesticVat),
        ],
        ["Total IVA deducible", null, summary.vat.deductibleVat],
        [null, null, null],
        [
          summary.vat.balance >= 0
            ? "Resultado: a ingresar"
            : "Resultado: a compensar / devolver",
          null,
          summary.vat.balance,
        ],
        [null, null, null],
        [
          "Estimación hecha por el CRM de Gula de Agua: revisar antes de presentar.",
          null,
          null,
        ],
      ],
    },
    {
      name: "Resultado",
      columns: [
        { header: "Concepto", width: 40 },
        { header: "Importe sin IVA" },
      ],
      rows: [
        ["Ingresos", summary.incomeBase],
        ["  de Stripe", summary.stripeBase],
        ["  manuales (TPV, financiera…)", summary.manualBase],
        ["Gastos", summary.expenseBase],
        ...summary.byCategory.map((row): (string | number)[] => [
          `  ${labelFor(EXPENSE_CATEGORIES, row.category)}`,
          row.base,
        ]),
        ["Resultado (ingresos - gastos)", summary.result],
      ],
    },
  ];

  if (period.quarter == null) {
    sheets.push({
      name: "Proveedores 347",
      columns: [
        { header: "Proveedor", width: 30 },
        { header: "NIF", width: 14 },
        { header: "Total del año" },
      ],
      rows: suppliersOver347(allExpenses, period.year).map((row) => [
        row.name,
        row.taxId,
        row.total,
      ]),
    });
  }
  return sheets;
};

const round = (value: number) => Math.round(value * 100) / 100;

/**
 * Zip for the accountant: the workbook plus every invoice and receipt of the
 * period, named as in the workbook.
 */
export const buildGestorPackage = async (
  period: Period,
  allExpenses: AccExpense[],
  allIncomes: IncomeRow[],
  download: (path: string) => Promise<Uint8Array>,
): Promise<{
  zip: Uint8Array;
  workbook: Uint8Array;
  workbookName: string;
  zipName: string;
  missing: string[];
}> => {
  const label = periodLabel(period).replace(/\s+/g, " ");
  const workbookName = `Contabilidad Gula ${label}.xlsx`;
  const workbook = zipSync(
    xlsxFiles(buildGestorSheets(period, allExpenses, allIncomes)),
  );
  const files: Zippable = {
    [workbookName]: [workbook, { level: 0 }],
  };
  const missing: string[] = [];

  const expenses = allExpenses
    .filter((e) => inPeriod(e.invoice_date, period))
    .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date));
  const manual = allIncomes
    .filter((row) => inPeriod(row.day, period))
    .sort((a, b) => a.day.localeCompare(b.day))
    .filter((row) => row.income?.file_path);

  const jobs = [
    ...expenses.flatMap((e, index) => expenseTargets(e, index)),
    ...manual.map((row, index) => ({
      path: row.income?.file_path as string,
      target: `${INCOME_FOLDER}/${packageFileName(index, row.day, row.customer || row.concept, null, row.income?.file_name)}`,
    })),
  ];
  for (const job of jobs) {
    try {
      files[job.target] = [await download(job.path), { level: 0 }];
    } catch {
      missing.push(job.target);
    }
  }
  return {
    zip: zipSync(files),
    workbook,
    workbookName,
    zipName: `Contabilidad Gula ${label}.zip`,
    missing,
  };
};
